import React, { useState, useEffect } from 'react';
import './ProfileCard.css';
import LevelProgressBar from './LevelProgressBar';
import LevelModal from './LevelModal';
import settingsIcon from '../../../assets/settings.svg';

function ProfileCard({ user, onOpenSettings }) {
  const [profileData, setProfileData] = useState(null);
  const [levelData, setLevelData] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [pointsHistory, setPointsHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredBar, setHoveredBar] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [showLevelModal, setShowLevelModal] = useState(false);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const [infoTooltipData, setInfoTooltipData] = useState(null);

  useEffect(() => {
    fetchProfileData();
    fetchLevelData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      
      const response = await fetch('/backend/api/dashboard/profile-stats.php', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setProfileData(data);
      }
    } catch (error) {
      console.error('Error fetching profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLevelData = async () => {
    try {
      // Fetch user's level data
      const levelResponse = await fetch('/backend/api/levels/my-level.php', {
        credentials: 'include'
      });
      
      if (levelResponse.ok) {
        const levelData = await levelResponse.json();
        if (levelData.success) {
          setLevelData(levelData);
        }
      }

      // Fetch leaderboard data
      const leaderboardResponse = await fetch('/backend/api/levels/leaderboard.php?limit=100', {
        credentials: 'include'
      });
      
      if (leaderboardResponse.ok) {
        const leaderboardData = await leaderboardResponse.json();
        if (leaderboardData.success) {
          setLeaderboardData(leaderboardData.leaderboard);
        }
      }

      // Fetch points history
      const historyResponse = await fetch('/backend/api/levels/history.php?days=30&limit=50', {
        credentials: 'include'
      });
      
      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        if (historyData.success) {
          setPointsHistory(historyData.history);
        }
      }
    } catch (error) {
      console.error('Error fetching level data:', error);
    }
  };

  const formatMemberSince = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleBarHover = (e, dayData) => {
    setHoveredBar(dayData);
    setTooltipPos({ x: e.clientX, y: e.clientY });
  };

  const handleBarLeave = () => {
    setHoveredBar(null);
  };

  const handleInfoIconHover = (e, pointsToNext) => {
    setShowInfoTooltip(true);
    setInfoTooltipData(pointsToNext);
    setTooltipPos({ x: e.clientX, y: e.clientY });
  };

  const handleInfoIconMove = (e) => {
    setTooltipPos({ x: e.clientX, y: e.clientY });
  };

  const handleInfoIconLeave = () => {
    setShowInfoTooltip(false);
    setInfoTooltipData(null);
  };

  if (loading) {
    return (
      <div className="profile-card">
        <div className="loading-spinner">Loading profile...</div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="profile-card">
        <div className="error-message">Failed to load profile data</div>
      </div>
    );
  }

  const { user: userData, stats, chart_data } = profileData;

  return (
    <div className="profile-card-wrapper">
      {/* NEW: Full-width Level Bar on top */}
      {levelData && (
        <div className="level-bar-container" onClick={() => setShowLevelModal(true)}>
          <LevelProgressBar 
            level={levelData.level_data.level}
            levelName={levelData.level_data.level_name}
            currentPoints={levelData.level_data.points_this_level}
            nextLevelPoints={levelData.level_data.points_this_level + levelData.level_data.points_to_next}
            rank={levelData.rank}
            onInfoIconHover={handleInfoIconHover}
            onInfoIconMove={handleInfoIconMove}
            onInfoIconLeave={handleInfoIconLeave}
          />
        </div>
      )}
      
      {/* Existing: Two 50% boxes below */}
      <div className="profile-bottom-row">
        {/* Section 1: Avatar + User Info (50%) */}
        <div className="profile-user-section">
          <div className="avatar-container">
            <img 
              src={`/assets/Profile/${userData.profile_avatar}`}
              alt="Profile Avatar"
              className="profile-avatar"
              onError={(e) => {
                e.target.src = '/assets/Profile/boy-1.svg';
              }}
            />
          </div>
          <div className="user-name-row">
            <div className="user-name">{userData.first_name} {userData.last_name}</div>
            <img 
              src={settingsIcon} 
              alt="Settings" 
              className="profile-settings-icon"
              onClick={onOpenSettings}
            />
          </div>
          <div className="user-details">
            <div className="detail-item">Member since: {formatMemberSince(userData.created_at)}</div>
            <div className="detail-item">Role: {userData.role}</div>
          </div>
        </div>

        {/* Section 2: Stats + Chart (50%) */}
        <div className="profile-chart-section">
          {/* Main Stats */}
          <div className="stats-header">
            <div className="main-stat">
              <span className="stat-number">{stats.total_tasks}</span>
            </div>
            <div className="trend-indicator">
              <svg className="trend-arrow" viewBox="0 0 24 24" fill="none">
                <path d="M7 14L12 9L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="trend-text">{stats.trend} upward</span>
            </div>
          </div>

          {/* Chart */}
          <div className="chart-container">
            <div className="chart-bars">
              {chart_data.map((day, index) => (
                <div key={index} className="chart-bar">
                  <div className="bar-background"></div>
                  <div 
                    className="bar-fill"
                    style={{ height: `${day.bar_height}px` }}
                    onMouseEnter={(e) => handleBarHover(e, day)}
                    onMouseMove={(e) => setTooltipPos({ x: e.clientX, y: e.clientY })}
                    onMouseLeave={handleBarLeave}
                  ></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Chart Tooltip */}
      {hoveredBar && (
        <div 
          className="chart-tooltip"
          style={{
            left: tooltipPos.x,
            top: tooltipPos.y
          }}
        >
          {hoveredBar.date}: {hoveredBar.count} tasks
        </div>
      )}

      {/* Info Tooltip */}
      {showInfoTooltip && infoTooltipData && (
        <div 
          className="chart-tooltip"
          style={{
            left: tooltipPos.x,
            top: tooltipPos.y
          }}
        >
          {infoTooltipData} Points to next level
        </div>
      )}

      {/* Level Modal */}
      <LevelModal 
        isOpen={showLevelModal}
        onClose={() => setShowLevelModal(false)}
        userLevelData={levelData}
        leaderboardData={leaderboardData}
        pointsHistory={pointsHistory}
      />
    </div>
  );
}

export default ProfileCard;
